import { AfterViewInit, Component, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener, OnInit, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ContentChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router, RouterModule } from '@angular/router';
import { FeatherModule } from 'angular-feather';

export interface NotificationItem {
  title: string;
  message: string;
  created_at: string;
  read: boolean;
  notification_type?: string;
  image_url?: string; // Profile/image URL for the notification
  avatar?: string; // Alternative property name for image
  [key: string]: any; // Allow additional properties
}

@Component({
  selector: 'app-notification-generic',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule, FeatherModule],
  templateUrl: './notification-generic.component.html',
  styleUrls: ['./notification-generic.component.scss'],
  host: {
    '[class.notification-generic--mobile]': 'isMobileView'
  }
})
export class NotificationGenericComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() notifications: NotificationItem[] = [];
  @Input() isOpen: boolean = false;
  @Input() viewAllRoute: string | any[] = [];
  @Input() viewAllLabel: string = 'TFNotification.ViewALL';
  @Input() viewAllMobileLabel: string = 'TFNotification.ViewAllMobile';
  @Input() markAllAsReadLabel: string = 'TFNotification.MarkAllAsRead';
  @Input() emptyStateMessage: string = 'TFNotification.NoNotifications';
  @Input() showMarkAllAsRead: boolean = true;
  @Input() showViewAll: boolean = true;
  @Input() panelWidth: string = '420px';
  @Input() maxHeight: string = '600px';

  @Output() close = new EventEmitter<void>();
  @Output() viewAllClick = new EventEmitter<void>();
  @Output() markAllAsReadClick = new EventEmitter<void>();
  @Output() notificationClick = new EventEmitter<NotificationItem>();
  @Output() customActionClick = new EventEmitter<{ action: string; notification: NotificationItem }>();

  @ViewChild('notificationDropdown') notificationDropdown!: ElementRef;
  @ViewChild('mobileNotificationsList') mobileNotificationsList?: ElementRef<HTMLElement>;
  @ViewChild('desktopNotificationsList') desktopNotificationsList?: ElementRef<HTMLElement>;
  @Input() triggerButtonRef?: ElementRef; // Optional reference to trigger button from parent
  @ContentChild('customActions') customActionsTemplate!: TemplateRef<any>;

  notificationTypes: string[] = [];
  unreadCounts: { [type: string]: number } = {};
  isMobileView = false;
  showScrollDown = false;
  showScrollUp = false;
  playEnterAnimation = false;
  mobileNotificationSections: { labelKey: string; items: NotificationItem[] }[] = [];
  private justOpened = false; // Flag to prevent immediate closing after opening
  private readonly visibleMobileCount = 4;
  private mobileListLayoutTimer: ReturnType<typeof setTimeout> | null = null;
  /** Phone width 320px–430px — same range as $fluid-vp-min / $fluid-vp-max in _fluid-scale.scss */
  private readonly mobileMediaQuery = window.matchMedia('(min-width: 320px) and (max-width: 430px)');
  private readonly onMobileViewportChange = (event: MediaQueryListEvent) => {
    this.isMobileView = event.matches;
    this.scheduleMobileListLayout(true);
  };

  constructor(
    public translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.isMobileView = this.mobileMediaQuery.matches;
    this.mobileMediaQuery.addEventListener('change', this.onMobileViewportChange);
    this.processNotifications();
  }

  ngAfterViewInit(): void {
    this.scheduleMobileListLayout(true);
  }

  ngOnDestroy(): void {
    this.clearMobileListLayoutTimer();
    this.mobileMediaQuery.removeEventListener('change', this.onMobileViewportChange);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notifications']) {
      this.processNotifications();
      this.scheduleMobileListLayout(false);
    }
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      // Set flag when opening to prevent immediate close
      this.justOpened = true;
      this.playEnterAnimation = true;
      setTimeout(() => {
        this.justOpened = false;
      }, 100);
      setTimeout(() => {
        this.playEnterAnimation = false;
      }, 500);
      this.scheduleMobileListLayout(true);
    }
  }

  onMobileListScroll(): void {
    this.updateMobileScrollHints();
  }

  private processNotifications(): void {
    if (!this.notifications || this.notifications.length === 0) {
      this.notificationTypes = [];
      this.unreadCounts = {};
      this.mobileNotificationSections = [];
      return;
    }

    this.notificationTypes = [
      ...new Set(this.notifications.map(n => n.notification_type).filter(Boolean))
    ];

    this.unreadCounts = {};
    this.notificationTypes.forEach(type => {
      this.unreadCounts[type] = this.notifications.filter(
        n => n.notification_type === type && n.read === false
      ).length;
    });

    this.mobileNotificationSections = this.buildMobileNotificationSections();
  }

  trackByNotificationId(index: number, notification: NotificationItem): string | number {
    const directId = notification?.['id'];
    if (directId != null) {
      return directId as string | number;
    }
    const message = notification?.['message'];
    if (message != null && typeof message === 'object' && !Array.isArray(message)) {
      const messageId = (message as { id?: string | number }).id;
      if (messageId != null) {
        return messageId;
      }
    }
    return `${notification?.title || ''}:${notification?.created_at || index}`;
  }

  trackBySectionLabel(_index: number, section: { labelKey: string }): string {
    return section.labelKey;
  }

  onViewAllClick(): void {
    this.viewAllClick.emit();
    this.close.emit();

    if (!this.showViewAll || this.viewAllRoute == null || this.viewAllRoute === '') {
      return;
    }

    const returnUrl = this.router.url.split('?')[0] || '/';
    const commands = Array.isArray(this.viewAllRoute) ? this.viewAllRoute : [this.viewAllRoute];
    void this.router.navigate(commands, { state: { returnUrl } });
  }

  onMarkAllAsReadClick(): void {
    this.markAllAsReadClick.emit();
  }

  onNotificationClick(notification: NotificationItem): void {
    this.notificationClick.emit(notification);
  }

  onCustomAction(action: string, notification: NotificationItem): void {
    this.customActionClick.emit({ action, notification });
  }

  getNotificationImage(notification: NotificationItem): string | null {
    return notification.image_url || notification.avatar || null;
  }

  onImageError(event: any): void {
    // Hide the image and show initials instead
    event.target.style.display = 'none';
    const avatarElement = event.target.parentElement;
    if (avatarElement) {
      const initialsSpan = avatarElement.querySelector('.avatar-initials');
      if (initialsSpan) {
        (initialsSpan as HTMLElement).style.display = 'flex';
      }
    }
  }

  getInitials(title: string): string {
    if (!title) return 'N';
    const words = title.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return title[0].toUpperCase();
  }

  getAvatarClass(index: number): string {
    const classes = ['orange', 'slate', 'blue'];
    return classes[index % classes.length];
  }

  formatNotificationTime(date: string): string {
    if (!date) return '';
    try {
      const notificationDate = new Date(date);
      if (isNaN(notificationDate.getTime())) return '';

      const now = new Date();
      const diffInMs = now.getTime() - notificationDate.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      const timeStr = notificationDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      if (diffInDays === 0) {
        return `Today at ${timeStr}`;
      } else if (diffInDays === 1) {
        return `Yesterday at ${timeStr}`;
      } else if (diffInDays < 7) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `Last ${days[notificationDate.getDay()]} at ${timeStr}`;
      } else {
        return (
          notificationDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }) + ` at ${timeStr}`
        );
      }
    } catch (e) {
      return '';
    }
  }

  getUnreadCountForType(type: string): number {
    return this.unreadCounts[type] || 0;
  }

  isFirstOfType(notification: NotificationItem, index: number): boolean {
    if (!this.notifications || index === 0) return true;
    // Check if this is the first unread notification of this type
    for (let i = 0; i < index; i++) {
      const prevNotif = this.notifications[i];
      if (prevNotif.notification_type === notification.notification_type && !prevNotif.read) {
        return false;
      }
    }
    return !notification.read;
  }

  private buildMobileNotificationSections(): { labelKey: string; items: NotificationItem[] }[] {
    const all = this.notifications || [];
    if (!all.length) {
      return [];
    }

    const today = all.filter(n => this.isToday(this.getNotificationDate(n)));
    const earlier = all.filter(n => !this.isToday(this.getNotificationDate(n)));
    const sections: { labelKey: string; items: NotificationItem[] }[] = [];

    if (today.length) {
      sections.push({ labelKey: 'TFNotification.Today', items: today });
    }
    if (earlier.length) {
      sections.push({ labelKey: 'TFNotification.Earlier', items: earlier });
    }

    if (!sections.length) {
      sections.push({ labelKey: 'TFNotification.Today', items: all });
    }

    return sections;
  }

  getNotificationDate(notification: NotificationItem): string | undefined {
    const value = notification?.created_at ?? notification?.['date'] ?? notification?.['timestamp'];
    return value != null ? String(value) : undefined;
  }

  private isToday(date: string | undefined): boolean {
    if (!date) return false;
    try {
      const notificationDate = new Date(date);
      if (isNaN(notificationDate.getTime())) return false;
      const now = new Date();
      return notificationDate.toDateString() === now.toDateString();
    } catch {
      return false;
    }
  }

  formatRelativeTime(date: string | undefined): string {
    if (!date) return '';
    try {
      const notificationDate = new Date(date);
      if (isNaN(notificationDate.getTime())) return '';

      const now = new Date();
      const diffInMs = now.getTime() - notificationDate.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInMinutes < 1) {
        return this.translate.instant('TFNotification.JustNow');
      }
      if (diffInMinutes < 60) {
        return this.translate.instant('TFNotification.MinutesAgo', { count: diffInMinutes });
      }
      if (diffInHours < 24) {
        return this.translate.instant('TFNotification.HoursAgo', { count: diffInHours });
      }
      if (diffInDays < 7) {
        return this.translate.instant('TFNotification.DaysAgo', { count: diffInDays });
      }

      return notificationDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  }

  getEmptyStateMessage(): string {
    const translated = this.translate.instant(this.emptyStateMessage);
    return translated !== this.emptyStateMessage ? translated : 'No notifications';
  }

  private scheduleMobileListLayout(resetScroll = false): void {
    this.clearMobileListLayoutTimer();
    this.mobileListLayoutTimer = setTimeout(() => {
      requestAnimationFrame(() => this.layoutMobileList(resetScroll));
    }, 50);
  }

  private clearMobileListLayoutTimer(): void {
    if (this.mobileListLayoutTimer != null) {
      clearTimeout(this.mobileListLayoutTimer);
      this.mobileListLayoutTimer = null;
    }
  }

  private getActiveNotificationList(): HTMLElement | undefined {
    return this.isMobileView
      ? this.mobileNotificationsList?.nativeElement
      : this.desktopNotificationsList?.nativeElement;
  }

  private layoutMobileList(resetScroll = false): void {
    const list = this.getActiveNotificationList();
    if (!this.isOpen || !list) {
      this.clearMobileListHeight(list);
      this.showScrollDown = false;
      this.showScrollUp = false;
      return;
    }

    this.clearMobileListHeight(list);

    if (resetScroll) {
      list.scrollTop = 0;
    }

    const itemSelector = this.isMobileView ? '.notification-item--mobile' : '.notification-item';
    const items = list.querySelectorAll<HTMLElement>(itemSelector);
    const visibleCount = Math.min(this.visibleMobileCount, items.length);

    if (visibleCount > 0) {
      const lastVisible = items[visibleCount - 1];
      const height = Math.ceil(lastVisible.offsetTop + lastVisible.offsetHeight);
      if (height > 0) {
        list.style.height = `${height}px`;
        list.style.maxHeight = `${height}px`;
      }
    }

    this.updateMobileScrollHints();
  }

  private clearMobileListHeight(list?: HTMLElement): void {
    if (!list) {
      return;
    }
    list.style.height = '';
    list.style.maxHeight = '';
  }

  private updateMobileScrollHints(): void {
    if (!this.isOpen) {
      this.showScrollDown = false;
      this.showScrollUp = false;
      return;
    }

    const list = this.getActiveNotificationList();
    if (!list) {
      this.showScrollDown = false;
      this.showScrollUp = false;
      return;
    }

    const threshold = 2;
    const { scrollTop, scrollHeight, clientHeight } = list;
    const canScroll = scrollHeight - clientHeight > threshold;
    const atBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    const showChevrons = this.isMobileView && canScroll;

    this.showScrollDown = showChevrons && !atBottom;
    this.showScrollUp = showChevrons && atBottom;
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: any): void {
    if (!this.isOpen || this.justOpened) return;

    const clickedInsideDropdown = this.notificationDropdown?.nativeElement?.contains(event.target);
    const clickedTrigger = this.triggerButtonRef?.nativeElement?.contains(event.target);

    if (!clickedInsideDropdown && !clickedTrigger) {
      this.close.emit();
    }
  }
}

